import { useEffect, useMemo, useState } from "react";

import { createInvitationSessionService } from "./create-invitation-session-service";
import InvitationSessionService, {
  type InvitationSession,
} from "./invitation-session-service";

export interface UseInvitationSessionOptions {
  /** `ext-sessionId` authorize param; absent when the customer did not open an invitation link. */
  sessionId: string | undefined;
  /** Injectable for tests; defaults to {@link createInvitationSessionService}. */
  service?: InvitationSessionService;
}

export interface UseInvitationSessionResult {
  /** `null` while loading, on failure, and when there is no invitation. */
  session: InvitationSession | null;
  isLoading: boolean;
  /** `true` when an invitation session was requested but could not be loaded. */
  hasError: boolean;
}

interface LoadResult {
  sessionId: string;
  session: InvitationSession | null;
}

/** Loads the KLS invitation session referenced by the authorize params. */
export function useInvitationSession({
  sessionId,
  service,
}: UseInvitationSessionOptions): UseInvitationSessionResult {
  // Reuse the same service instance unless tests inject a replacement.
  const invitationService = useMemo<InvitationSessionService>(
    () => service ?? createInvitationSessionService(),
    [service],
  );
  const [loadResult, setLoadResult] = useState<LoadResult | null>(null);

  useEffect(() => {
    if (!sessionId) {
      return;
    }

    let cancelled = false;
    invitationService.getSession(sessionId).then(
      (session) => {
        if (!cancelled) {
          setLoadResult({ sessionId, session });
        }
      },
      () => {
        if (!cancelled) {
          setLoadResult({ sessionId, session: null });
        }
      },
    );

    return () => {
      // Session ID or service changed — discard the in-flight response.
      cancelled = true;
    };
  }, [sessionId, invitationService]);

  if (!sessionId) {
    return { session: null, isLoading: false, hasError: false };
  }
  // Results are keyed by session ID so a previous ID's result is never shown.
  if (loadResult?.sessionId !== sessionId) {
    return { session: null, isLoading: true, hasError: false };
  }
  return {
    session: loadResult.session,
    isLoading: false,
    hasError: loadResult.session === null,
  };
}
